/**
 * TypeScript wrapper for WASM per-loop normal computation.
 * Shared-vertex model — no vertex splitting, no weld maps.
 */
import {
  recompute_loop_normals as wasmRecomputeLoopNormals,
  recompute_loop_normals_partial as wasmRecomputeLoopNormalsPartial,
} from "./pkg/poseref_wasm.js";

/** Inclusive corner-index range returned by `recomputeLoopNormalsPartial`. */
export interface NormalsPartialRange {
  /** Smallest corner index that was zeroed and recomputed. */
  minCorner: number;
  /** Largest corner index that was zeroed and recomputed. */
  maxCorner: number;
}

import type { LoopNormals } from "../modeling/normalTypes.ts";
import { asLoopNormals } from "../modeling/normalTypes.ts";
import type { PolygonTopology } from "../modeling/types.ts";

export {
  AUTO_SMOOTH_ANGLE_DEFAULT,
  resolveAutoSmoothAngle,
} from "../modeling/ops/normals.ts";

/**
 * Compute per-loop (per face-corner) normals with auto-smooth.
 *
 * Detects the normal domain and dispatches to the cheapest computation:
 * - Face domain: O(F) — each corner copies its face normal
 * - Point domain: O(C) — angle-weighted vertex accumulation, no union-find
 * - Corner domain: full auto-smooth with smooth-group union-find
 *
 * Returns LoopNormals of length topology.corners.length × 3.
 */
export function recomputeLoopNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
  autoSmoothAngle: number = 30,
): LoopNormals {
  if (import.meta.env.DEV) {
    if (vertices.length % 3 !== 0) {
      throw new Error(
        `recomputeLoopNormals: vertices length ${vertices.length} not divisible by 3`,
      );
    }
    if (topology.offsets.length < 1) {
      throw new Error("recomputeLoopNormals: offsets array is empty");
    }
    const lastOffset = topology.offsets[topology.offsets.length - 1];
    if (lastOffset !== topology.corners.length) {
      throw new Error(
        `recomputeLoopNormals: last offset ${lastOffset} !== corners.length ${topology.corners.length}`,
      );
    }
    const vertexCount = vertices.length / 3;
    for (let i = 0; i < topology.corners.length; i++) {
      if (topology.corners[i] >= vertexCount) {
        throw new Error(
          `recomputeLoopNormals: corner[${i}] = ${topology.corners[i]} >= vertexCount ${vertexCount}`,
        );
      }
    }
  }
  const result = wasmRecomputeLoopNormals(
    vertices,
    topology.offsets,
    topology.corners,
    autoSmoothAngle,
    topology.edgeFlags,
    topology.cornerEdges,
  );
  const normals = result.normals;
  result.free();
  return asLoopNormals(normals);
}

/**
 * Incrementally recompute per-loop normals for corners touching affected vertices.
 * Mutates `loopNormals` in place. Used by sculpt brushes for fast updates.
 *
 * Returns the inclusive corner-index range of every slot that was dirtied and
 * recomputed. The caller uses this to issue a tight partial GPU upload instead
 * of re-uploading the full normal buffer.
 *
 * The `loopNormals` parameter MUST be a per-corner buffer (LoopNormals), not a
 * per-vertex buffer. Passing a per-vertex buffer causes out-of-bounds WASM traps
 * because corner indices exceed the buffer length.
 */
export function recomputeLoopNormalsPartial(
  vertices: Float32Array,
  loopNormals: LoopNormals,
  affectedVerts: number[],
  topology: PolygonTopology,
  autoSmoothAngle: number = 30,
): NormalsPartialRange {
  if (import.meta.env.DEV) {
    const cornerCount = topology.corners.length;
    if (loopNormals.length !== cornerCount * 3) {
      throw new Error(
        `recomputeLoopNormalsPartial: loopNormals length ${loopNormals.length} !== ` +
          `cornerCount*3 (${cornerCount * 3}). Per-vertex buffer passed instead of per-loop?`,
      );
    }
    const vertexCount = vertices.length / 3;
    for (const vi of affectedVerts) {
      if (vi < 0 || vi >= vertexCount) {
        throw new Error(
          `recomputeLoopNormalsPartial: affected vertex ${vi} out of bounds (vertexCount=${vertexCount})`,
        );
      }
    }
  }
  const result = wasmRecomputeLoopNormalsPartial(
    vertices,
    loopNormals,
    new Uint32Array(affectedVerts),
    topology.offsets,
    topology.corners,
    autoSmoothAngle,
  );
  const minCorner = result.min_dirty_corner;
  const maxCorner = result.max_dirty_corner;
  result.free();
  return { minCorner, maxCorner };
}
